import { useCallback } from 'react';
import { useDemoDateLock } from './useDemoDateLock';
import { useToast } from '@/components/ui/use-toast';

interface UseDemoModeSettingsReturn {
  isDemoMode: boolean;
  showDemoToast: () => void;
}

/**
 * Hook to manage DEMO mode for Account Settings
 *
 * Provides:
 * - isDemoMode: Boolean indicating if app is running in DEMO mode
 * - showDemoToast: Function to display toast notification when editing is attempted
 *
 * Usage:
 * ```tsx
 * const { isDemoMode, showDemoToast } = useDemoModeSettings();
 *
 * <button
 *   onClick={isDemoMode ? showDemoToast : handleEdit}
 *   disabled={isDemoMode}
 * >
 *   Edit
 * </button>
 * ```
 *
 * @returns {UseDemoModeSettingsReturn} Demo mode state and utilities
 */
export const useDemoModeSettings = (): UseDemoModeSettingsReturn => {
  const { isDemoMode } = useDemoDateLock();
  const { toast } = useToast();

  const showDemoToast = useCallback(() => {
    toast({
      title: 'DEMO Mode - Editing Disabled',
      description: 'Editing is not allowed in the demo version.',
      variant: 'default',
    });
  }, [toast]);

  return {
    isDemoMode,
    showDemoToast,
  };
};
